'use client'

import * as React from 'react'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import type { StartSubscriptionProps } from './hooks/use-autostart-subscription'
import { useStartSubscription } from './hooks/use-autostart-subscription'
import type { PAYMENT_ENTRY_POINTS } from '../payments-dialog/analytics'
import { savePaymentModalEntryPoint } from '../payments-dialog/analytics'
import { tw } from 'utils/classnames'
import { useIsLoggedInQuery } from 'auth/hooks'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { defaultLanguage } from 'cf-i18n/constants'
import type { SubscriptionPlan } from 'codegen/types'
import { getStartSubscriptionLoggedOutRedirectPath } from '../utils'
import { Slot } from 'primitives/slot'

type StartSubscriptionButtonProps = StartSubscriptionProps & {
  style?: React.CSSProperties
  entryPoint: PAYMENT_ENTRY_POINTS
  className?: string
  onClick?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void
  enableKeyboardSubmit?: boolean
  asChild?: boolean
  children?: React.ReactNode
}

function getRedirectUrl(subscriptionPlan: SubscriptionPlan, isTrial: boolean) {
  return getStartSubscriptionLoggedOutRedirectPath(subscriptionPlan, isTrial) ?? ''
}

export function StartSubscriptionButton({
  entryPoint,
  className,
  onClick,
  onKeyDown,
  redirectUrl,
  style = {},
  enableKeyboardSubmit = false,
  asChild = false,
  children,
  ...hookProps
}: StartSubscriptionButtonProps) {
  const locale = useSafeLocale()
  const isLoggedInQuery = useIsLoggedInQuery()

  const { startSubscription, isPending } = useStartSubscription({ redirectUrl, ...hookProps })

  const handleSubscriptionClick = React.useCallback(async () => {
    if (isPending) {
      return
    }

    savePaymentModalEntryPoint(entryPoint)

    if (onClick) {
      onClick()
    }

    await startSubscription()
  }, [entryPoint, onClick, isPending, startSubscription])

  const handleKeyboardSubmit = React.useCallback(
    async (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isPending) {
        return
      }

      if (enableKeyboardSubmit) {
        e.preventDefault()
        savePaymentModalEntryPoint(entryPoint)

        if (onKeyDown) {
          onKeyDown(e)
        }

        await startSubscription()
      }
    },
    [entryPoint, onKeyDown, enableKeyboardSubmit, isPending, startSubscription],
  )

  let redirectUrlFinal = getRedirectUrl(hookProps.subscriptionPlan, hookProps.isTrial)

  if (redirectUrl) {
    redirectUrlFinal = redirectUrl
  }

  redirectUrlFinal = addOrReplaceLocaleInUrl(redirectUrlFinal, locale)

  if (!isLoggedInQuery.data && redirectUrlFinal.length > 0) {
    if (asChild) {
      const loggedOutSlotProps = {
        className,
        style,
        href: redirectUrlFinal,
      } as React.ComponentPropsWithoutRef<typeof Slot>

      return (
        <Slot {...loggedOutSlotProps} onClick={onClick}>
          {children}
        </Slot>
      )
    }

    return (
      <a
        onClick={onClick}
        style={style}
        href={redirectUrlFinal}
        className={tw('relative flex items-center justify-center', className)}
      >
        {children}
      </a>
    )
  }

  if (asChild) {
    return (
      <Slot
        style={style}
        onClick={handleSubscriptionClick}
        onKeyDown={handleKeyboardSubmit}
        className={className}
      >
        <div className="relative flex w-full items-center justify-center">
          <div
            className={tw(
              'flex items-center justify-center w-full',
              isPending ? 'invisible' : 'visible',
            )}
          >
            {children}
          </div>
          {isPending ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <AnimatedSpinnerIcon />
            </span>
          ) : null}
        </div>
      </Slot>
    )
  }

  return (
    <button
      style={style}
      onClick={handleSubscriptionClick}
      onKeyDown={handleKeyboardSubmit}
      disabled={isPending}
      className={tw('relative flex items-center justify-center', className)}
    >
      <div className={isPending ? 'invisible' : 'visible'}>{children}</div>
      {isPending ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <AnimatedSpinnerIcon />
        </span>
      ) : null}
    </button>
  )
}

function useSafeLocale() {
  if (typeof document === 'undefined') {
    return defaultLanguage
  }

  return document.documentElement.lang || defaultLanguage
}
